import { withPrefix } from 'gatsby-link';
import { isCountryCode, convertCountryCode } from './country-mapping.js';
import config from '../../../site-config';

const THREE = require('three');
const OrbitControls = require('trustkit-three-orbit-controls')(THREE);
const MeshLine = require('three.meshline');

let canvas, scene, renderer, data, globe;

// Cache DOM selectors
let container;

// Object for geolocation HTML elements and variables
const elements = {};

// Three group objects
const groups = {
  main: null, // A group containing everything
  globe: null, // A group containing the globe sphere (and globe dots)
  globeDots: null, // A group containing the globe dots
  lines: null, // A group containing the lines between each geolocation
  lineDots: null, // A group containing the line dots
};

// Map properties for creation and rendering
const props = {
  // Toolbar header height
  headerHeight: 64,
  mapSize: {
    // Size of the map from the intial source image (on which the dots are positioned on)
    width: 1024,
    height: 512,
  },
  globeRadius: 200, // Radius of the globe (used for many calculations)
  dotsAmount: 30, // Amount of dots to generate and animate randomly across the lines
  colours: {
    lines: new THREE.Color('#E54530'),
    lineDots: new THREE.Color('#FFA9A3'),
  },
  alphas: {
    // Transparency
    globe: 0.3,
    lines: 1.0,
  },
  hqLoc: null,
  citiesData: null,
  toggleState: null,
};

// Angles used for animating the camera
const camera = {
  object: null, // Three object of the camera
  controls: null, // Three object of the orbital controls
  angles: {
    // Object of the camera angles for animating
    current: {
      azimuthal: null,
      polar: null,
    },
    target: {
      azimuthal: null,
      polar: null,
    },
  },
};

// Booleans and values for animations
const animations = {
  finishedIntro: false, // Boolean of when the intro animations have finished
  dots: {
    current: 0, // Animation frames of the globe dots introduction animation
    total: 170, // Total frames (duration) of the globe dots introduction animation,
    points: [], // Array to clone the globe dots coordinates to
  },
  globe: {
    current: 0, // Animation frames of the globe introduction animation
    total: 80, // Total frames (duration) of the globe introduction animation,
  },
  geolocations: {
    atHq: true,
    active: false, // Boolean if the geolocation elements have been added and made active
    animating: false, // Boolean if the geolocations are currently being animated
    current: 0, // Animation frames of geolocation elements introduction animation
    total: 240, // Total frames (duration) of the geolocation elements introduction animation
    selected: null, // Three group object of the currently selected geolocation
    index: null, // Index of the geolocation in the data array
    timeout: null, // Timeout object for cycling to the next geolocation
    initialDuration: 2000, // Initial timeout duration before starting the geolocation cycle
    duration: 1000, // Timeout duration between cycling to the next geolocation
  },
};

// Boolean to enable or disable rendering when window is in or out of focus
let isHidden = false;

function setupScene() {
  container = document.getElementsByClassName('js-globe')[0];
  canvas = container.getElementsByClassName('js-canvas')[0];

  scene = new THREE.Scene();
  renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    alpha: true,
    shadowMapEnabled: false,
  });
  renderer.setSize(
    canvas.clientWidth / 2,
    canvas.clientHeight - props.headerHeight,
  );
  renderer.setPixelRatio(1);
  renderer.setClearColor(0x000000, 0);

  // Main group that contains everything
  groups.main = new THREE.Group();
  groups.main.name = 'Main';

  // Group that contains lines for each geolocation
  groups.lines = new THREE.Group();
  groups.lines.name = 'Lines';
  groups.main.add(groups.lines);

  // Group that contains dynamically created dots
  groups.lineDots = new THREE.Group();
  groups.lineDots.name = 'Dots';
  groups.main.add(groups.lineDots);

  // Add the main group to the scene
  scene.add(groups.main);

  // Render camera and add orbital controls
  addCamera();
  addControls();

  // Render objects
  addGlobe();
  addLines();
  createListElements();

  // Start the requestAnimationFrame loop
  render();
  animate();

  const canvasResizeBehaviour = function() {
    container.width = window.innerWidth / 2;
    container.height = window.innerHeight - props.headerHeight;
    container.style.width = `${window.innerWidth}px`;
    container.style.height = `${window.innerHeight}px`;

    const effecOffsetWidth = container.offsetWidth ;
    const effecOffsetHeight = container.offsetHeight - props.headerHeight;

    camera.object.aspect = effecOffsetWidth / effecOffsetHeight;
    camera.object.updateProjectionMatrix();
    renderer.setSize(effecOffsetWidth, effecOffsetHeight);
  };

  window.addEventListener('resize', canvasResizeBehaviour);
  window.addEventListener('orientationchange', () => {
    setTimeout(canvasResizeBehaviour, 0);
  });
  canvasResizeBehaviour();
}

function addCamera() {
  camera.object = new THREE.PerspectiveCamera(
    60,
    canvas.clientWidth / 2 / (canvas.clientHeight - props.headerHeight),
    1,
    10000,
  );
  camera.object.position.z = props.globeRadius * 2.2;
}

function addControls() {
  camera.controls = new OrbitControls(camera.object, canvas);
  camera.controls.enableKeys = false;
  camera.controls.enablePan = false;
  camera.controls.enableZoom = true;
  camera.controls.enableDamping = false;
  camera.controls.enableRotate = true;

  camera.zoom = 0.5;
  camera.controls.zoomSpeed = 0.5;
  camera.controls.minDistance = 250;
  camera.controls.maxDistance = 600;

  // Set the initial camera angles to something crazy for the introduction animation
  camera.angles.current.azimuthal = -Math.PI;
  camera.angles.current.polar = 0;
}

function render() {
  renderer.render(scene, camera.object);
}

function onFocusChange(event) {
  const visible = 'visible';
  const hidden = 'hidden';
  const eventMap = {
    focus: visible,
    focusin: visible,
    pageshow: visible,
    blur: hidden,
    focusout: hidden,
    pagehide: hidden,
  };

  event = event || window.event;

  if (event.type in eventMap) {
    isHidden = true;
  } else {
    isHidden = false;
  }
}

function animate() {
  if (isHidden === false) {
    requestAnimationFrame(animate);
  }

  if (groups.globeDots) {
    introAnimate();
  }

  if (animations.finishedIntro === true) {
    animateDots();
  }

  if (animations.geolocations.animating === true) {
    animateGeolocationCycle();
  }

  positionElements();
  camera.controls.update();
  render();
}

function addGlobe() {
  const textureLoader = new THREE.TextureLoader();
  textureLoader.setCrossOrigin(true);

  const radius = props.globeRadius - props.globeRadius * 0.02;
  const segments = 64;
  const rings = 64;

  const canvasSize = 128;
  const textureCanvas = document.createElement('canvas');
  textureCanvas.width = canvasSize;
  textureCanvas.height = canvasSize;
  const canvasContext = textureCanvas.getContext('2d');
  canvasContext.rect(0, 0, canvasSize, canvasSize);

  // Make texture
  const texture = new THREE.Texture(textureCanvas);
  texture.needsUpdate = true;

  const geometry = new THREE.SphereGeometry(radius, segments, rings);

  const material = new THREE.MeshPhongMaterial({
    map: textureLoader.load(withPrefix('/img/2_no_clouds_8k.jpg')),
    bumpMap: textureLoader.load(withPrefix('/img/elev_bump_8k.jpg')),
    bumpScale: 0.5,
    specularMap: textureLoader.load(withPrefix('/img/water_8k.png')),
    specular: new THREE.Color('grey'),
  });

  scene.add(new THREE.AmbientLight(0xf0f0f0));

  const light = new THREE.DirectionalLight(0x404040, 0.3);
  light.position.set(5, 3, 5);
  scene.add(light);

  globe = new THREE.Mesh(geometry, material);

  groups.globe = new THREE.Group();
  groups.globe.name = 'Globe';

  groups.globe.add(globe);
  groups.main.add(groups.globe);

  addGlobeDots();
}

function addGlobeDots() {
  const geometry = new THREE.Geometry();

  // Make circle
  const canvasSize = 16;
  const halfSize = canvasSize / 2;
  const textureCanvas = document.createElement('canvas');
  textureCanvas.width = canvasSize;
  textureCanvas.height = canvasSize;
  const canvasContext = textureCanvas.getContext('2d');
  canvasContext.beginPath();
  canvasContext.arc(halfSize, halfSize, halfSize, 0, 2 * Math.PI);
  canvasContext.fillStyle = props.colours.globeDots;
  canvasContext.fill();

  // Make texture
  const texture = new THREE.Texture(textureCanvas);
  texture.needsUpdate = true;

  const material = new THREE.PointsMaterial({
    map: texture,
    size: props.globeRadius / 90,
  });

  const addDot = function(targetX, targetY) {
    // Add a point with zero coordinates
    const point = new THREE.Vector3(0, 0, 0);
    geometry.vertices.push(point);

    // Add the coordinates to a new array for the intro animation
    const result = latLongToVector3(targetX, targetY);
    animations.dots.points.push(
      new THREE.Vector3(result.x, result.y, result.z),
    );
  };

  addDot(props.hqLoc.lat, props.hqLoc.long);

  for (const i in props.citiesData) {
    addDot(props.citiesData[i].lat, props.citiesData[i].long);
  }

  // Add the points to the scene
  groups.globeDots = new THREE.Points(geometry, material);
  groups.globe.add(groups.globeDots);
}

/* COUNTRY LINES AND DOTS */

function addLines() {
  // Create the geometry
  const geometry = new THREE.Geometry();
  const group = new THREE.Group();
  group.name = 'hq';

  for (const i in props.citiesData) {
    const cityEnd = props.citiesData[i];
    const isOutsideUS =
      cityEnd.city == cityEnd.city.toUpperCase() && cityEnd.city.length == 2;

    // Get the spatial coordinates
    const result = returnCurveCoordinates(
      props.hqLoc.lat,
      props.hqLoc.long,
      cityEnd.lat,
      cityEnd.long,
      isOutsideUS,
    );

    // Calculate the curve in order to get points from start to end
    const curve = new THREE.QuadraticBezierCurve3(
      new THREE.Vector3(result.start.x, result.start.y, result.start.z),
      new THREE.Vector3(result.mid.x, result.mid.y, result.mid.z),
      new THREE.Vector3(result.end.x, result.end.y, result.end.z),
    );

    // Get verticies from curve
    geometry.vertices = curve.getPoints(200);

    // Create mesh line using plugin and set its geometry
    const line = new MeshLine.MeshLine();
    line.setGeometry(geometry);

    // Create the mesh line material using the plugin
    const material = new MeshLine.MeshLineMaterial({
      color: props.colours.lines,
      transparent: true,
      opacity: props.alphas.lines,
      lineWidth: 0.4,
    });

    // Create the final object to add to the scene
    const curveObject = new THREE.Mesh(line.geometry, material);
    curveObject._path = geometry.vertices;

    group.add(curveObject);
  }
  group.visible = false;
  groups.lines.add(group);
}

function addLineDots() {
  const radius = props.globeRadius / 180;
  const segments = 8;
  const rings = 8;

  const geometry = new THREE.SphereGeometry(radius, segments, rings);
  const material = new THREE.MeshBasicMaterial({
    color: props.colours.lineDots,
  });

  // Returns a sphere geometry positioned at coordinates
  const returnLineDot = function() {
    const sphere = new THREE.Mesh(geometry, material);
    return sphere;
  };

  for (let i = 0; i < props.dotsAmount; i++) {
    // Get the city path geometry vertices and create the dot at the first vertex
    const targetDot = returnLineDot();
    targetDot.visible = false;

    // Add custom variables for custom path coordinates and index
    targetDot._pathIndex = null;
    targetDot._path = null;

    // Add the dot to the dots group
    groups.lineDots.add(targetDot);
  }
}

function assignDotsToRandomLine(target) {
  // Get a random line from the current geolocation
  let randomLine =
    Math.random() * (animations.geolocations.selected.children.length - 1);
  randomLine = animations.geolocations.selected.children[randomLine.toFixed(0)];
  // Assign the random geolocation path to the dot and set the index at 0
  target._path = randomLine._path;
}

function reassignDotsToNewLines() {
  for (let i = 0; i < groups.lineDots.children.length; i++) {
    const target = groups.lineDots.children[i];
    if (target._path !== null && target._pathIndex !== null) {
      assignDotsToRandomLine(target);
    }
  }
}

function animateDots() {
  // Loop through the dots children group
  for (let i = 0; i < groups.lineDots.children.length; i++) {
    const dot = groups.lineDots.children[i];
    if (dot._path === null) {
      // Create a random seed as a pseudo-delay
      const seed = Math.random();
      if (seed > 0.99) {
        assignDotsToRandomLine(dot);
        dot._pathIndex = dot._path.length - 1;
      }
    } else if (dot._path !== null && dot._pathIndex > 0) {
      // Show the dot
      if (dot.visible === false) {
        dot.visible = true;
      }
      // Move the dot along the path vertice coordinates
      dot.position.x = dot._path[dot._pathIndex].x;
      dot.position.y = dot._path[dot._pathIndex].y;
      dot.position.z = dot._path[dot._pathIndex].z;
      // Advance the dot along the path (in reverse)
      dot._pathIndex--;
    } else {
      // Hide the dot
      dot.visible = false;
      // Remove the path assignment
      dot._path = null;
    }
  }
}

let list;

function createListElements() {
  list = document.getElementsByClassName('js-list')[0];

  const toTitleCase = function(str) {
    return str.replace(
      /\w\S*/g,
      txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase(),
    );
  };

  const pushObject = function(coordinates, target) {
    // Create the element
    const element = document.createElement('li');
    let placeLabel;
    if (target == 'HQ') {
      placeLabel = target;
    } else if (isCountryCode(target)) {
      placeLabel = convertCountryCode(target);
    } else {
      placeLabel = toTitleCase(target);
    }
    element.innerHTML = `<span class="place-label">${placeLabel}</span>`;
    const object = {
      position: coordinates,
      element,
    };
    // Add the element to the DOM and add the object to the array
    list.appendChild(element);
    elements[target] = object;
  };

  const hqLocLatRounded = Math.round(props.hqLoc.lat * 10) / 10;
  const hqLocLongRounded = Math.round(props.hqLoc.long * 10) / 10;

  // Create location labels
  for (const i in props.citiesData) {
    const cityData = props.citiesData[i];
    // If HQ coordinates (rounded to 1dp) is same as one of the cities, print 'HQ' instead of city name
    if (
      Math.round(cityData.lat * 10) / 10 == hqLocLatRounded &&
      Math.round(cityData.long * 10) / 10 == hqLocLongRounded
    ) {
      continue;
    }
    var coordinates = latLongToVector3(cityData.lat, cityData.long);
    pushObject(
      new THREE.Vector3(coordinates.x, coordinates.y, coordinates.z),
      cityData.city,
    );
  }

  var coordinates = latLongToVector3(props.hqLoc.lat, props.hqLoc.long);
  pushObject(
    new THREE.Vector3(coordinates.x, coordinates.y, coordinates.z),
    'HQ',
  );

  // Set the geolocation cycle index and selected line object for the starting geolocation
  animations.geolocations.index = props.citiesData.length;
  animations.geolocations.selected = groups.lines.getObjectByName('hq');
  // Set the line opacity to 0 so they can be faded-in during the introduction animation
  const lineGroup = animations.geolocations.selected;
  lineGroup.visible = true;
  for (let ii = 0; ii < lineGroup.children.length; ii++) {
    lineGroup.children[ii].material.uniforms.opacity.value = 0;
  }
  // Set the target camera angles for the starting geolocation for the introduction animation
  const angles = returnCameraAngles(props.hqLoc.lat, props.hqLoc.long);
  camera.angles.target.azimuthal = angles.azimuthal;
  camera.angles.target.polar = angles.polar;
}

function positionElements() {
  const widthHalf = canvas.clientWidth / 2;
  const heightHalf = canvas.clientHeight / 2;

  // Loop through the elements array and reposition the elements
  for (const key in elements) {
    const targetElement = elements[key];
    const position = getProjectedPosition(
      widthHalf,
      heightHalf,
      targetElement.position,
    );
    // Construct the X and Y position strings
    const positionX = `${position.x}px`;
    const positionY = `${position.y}px`;

    const elementStyle = targetElement.element.style;

    // Hide the dot/label if overflows canvas
    const labelElement = targetElement.element.getElementsByClassName(
      'place-label',
    )[0];
    // This magic number '32' is obtained from corresponding style-sheet variable $dotSizeLarge
    if (position.x < labelElement.clientWidth + 32) {
      elementStyle.visibility = 'hidden';
    } else {
      elementStyle.visibility = 'visible';
    }

    // Construct the 3D translate string
    elementStyle.webkitTransform = `translate3D(${positionX}, ${positionY}, 0)`;
    elementStyle.WebkitTransform = `translate3D(${positionX}, ${positionY}, 0)`;
    elementStyle.mozTransform = `translate3D(${positionX}, ${positionY}, 0)`;
    elementStyle.msTransform = `translate3D(${positionX}, ${positionY}, 0)`;
    elementStyle.oTransform = `translate3D(${positionX}, ${positionY}, 0)`;
    elementStyle.transform = `translate3D(${positionX}, ${positionY}, 0)`;
  }
}

/* INTRO ANIMATIONS */
// Easing reference: https://gist.github.com/gre/1650294

const easeInOutCubic = function(t) {
  return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
};

const easeOutCubic = function(t) {
  return --t * t * t + 1;
};

const easeInOutQuad = function(t) {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
};

function introAnimate() {
  if (animations.dots.current <= animations.dots.total) {
    const points = groups.globeDots.geometry.vertices;
    const totalLength = points.length;

    for (let i = 0; i < totalLength; i++) {
      // Get ease value
      let dotProgress = easeInOutCubic(
        animations.dots.current / animations.dots.total,
      );

      // Add delay based on loop iteration
      dotProgress += dotProgress * (i / totalLength);

      if (dotProgress > 1) {
        dotProgress = 1;
      }

      // Move the point
      points[i].x = animations.dots.points[i].x * dotProgress;
      points[i].y = animations.dots.points[i].y * dotProgress;
      points[i].z = animations.dots.points[i].z * dotProgress;

      // Animate the camera at the same rate as the first dot
      if (i === 0) {
        let azimuthalDifference =
          (camera.angles.current.azimuthal - camera.angles.target.azimuthal) *
          dotProgress;
        azimuthalDifference =
          camera.angles.current.azimuthal - azimuthalDifference;
        camera.controls.setAzimuthalAngle(azimuthalDifference);

        let polarDifference =
          (camera.angles.current.polar - camera.angles.target.polar) *
          dotProgress;
        polarDifference = camera.angles.current.polar - polarDifference;
        camera.controls.setPolarAngle(polarDifference);
      }
    }
    animations.dots.current++;

    // Update verticies
    groups.globeDots.geometry.verticesNeedUpdate = true;
  }

  if (
    animations.dots.current >= animations.dots.total * 0.65 &&
    animations.globe.current <= animations.globe.total
  ) {
    const globeProgress = easeOutCubic(
      animations.globe.current / animations.globe.total,
    );
    globe.material.opacity = props.alphas.globe * globeProgress;

    // Fade-in the geolocation lines
    const lines = animations.geolocations.selected.children;
    for (let ii = 0; ii < lines.length; ii++) {
      lines[ii].material.uniforms.opacity.value =
        props.alphas.lines * globeProgress;
    }

    animations.globe.current++;
  }

  if (
    animations.dots.current >= animations.dots.total * 0.7 &&
    animations.geolocations.active === false
  ) {
    list.classList.add('active');
    animations.geolocations.active = true;
  }

  if (
    animations.geolocations.active === true &&
    animations.finishedIntro === false
  ) {
    animations.finishedIntro = true;
    // Start geolocation cycle
    animations.geolocations.timeout = setTimeout(
      showNextGeolocation,
      animations.geolocations.initialDuration,
    );
    addLineDots();
  }
}

function changeGeolocation(city, init) {
  if (init !== true) {
    camera.angles.current.azimuthal = camera.controls.getAzimuthalAngle();
    camera.angles.current.polar = camera.controls.getPolarAngle();

    const targetAngles = returnCameraAngles(city.lat, city.long);
    camera.angles.target.azimuthal = targetAngles.azimuthal;
    camera.angles.target.polar = targetAngles.polar;

    animations.geolocations.animating = true;
  }
}

function animateGeolocationCycle() {
  if (animations.geolocations.current <= animations.geolocations.total) {
    const progress = easeInOutQuad(
      animations.geolocations.current / animations.geolocations.total,
    );

    let azimuthalDifference =
      (camera.angles.current.azimuthal - camera.angles.target.azimuthal) *
      progress;
    azimuthalDifference = camera.angles.current.azimuthal - azimuthalDifference;
    camera.controls.setAzimuthalAngle(azimuthalDifference);

    let polarDifference =
      (camera.angles.current.polar - camera.angles.target.polar) * progress;
    polarDifference = camera.angles.current.polar - polarDifference;
    camera.controls.setPolarAngle(polarDifference);

    animations.geolocations.current++;
  } else {
    animations.geolocations.animating = false;
    animations.geolocations.current = 0;
    animations.geolocations.timeout = setTimeout(
      showNextGeolocation,
      animations.geolocations.duration,
    );
  }
}

function showNextGeolocation() {
  let city;
  if (animations.geolocations.atHq) {
    animations.geolocations.index++;
    if (animations.geolocations.index >= props.citiesData.length) {
      animations.geolocations.index = 0;
    }
    city = props.citiesData[animations.geolocations.index];
    animations.geolocations.atHq = false;
  } else {
    city = props.hqLoc;
    animations.geolocations.atHq = true;
  }
  changeGeolocation(city, false);
}

/* COORDINATE CALCULATIONS */

function latLongToVector3(lat, lon) {
  const radius = props.globeRadius - props.globeRadius * 0.02;
  const phi = (lat * Math.PI) / 180;
  const theta = ((lon - 180) * Math.PI) / 180;

  const x = -radius * Math.cos(phi) * Math.cos(theta);
  const y = radius * Math.sin(phi);
  const z = radius * Math.cos(phi) * Math.sin(theta);

  return { x, y, z };
}

// Reference: https://codepen.io/ya7gisa0/pen/pisrm?editors=0010
function returnCurveCoordinates(
  latitudeA,
  longitudeA,
  latitudeB,
  longitudeB,
  isOutsideUS,
) {
  // Calculate the starting point
  const start = latLongToVector3(latitudeA, longitudeA);

  // Calculate the end point
  const end = latLongToVector3(latitudeB, longitudeB);

  // Calculate the mid-point
  const midPointX = (start.x + end.x) / 2;
  const midPointY = (start.y + end.y) / 2;
  const midPointZ = (start.z + end.z) / 2;

  // Calculate the distance between the two coordinates
  let distance = Math.pow(end.x - start.x, 2);
  distance += Math.pow(end.y - start.y, 2);
  distance += Math.pow(end.z - start.z, 2);
  distance = Math.sqrt(distance);

  // Calculate the multiplication value
  let multipleVal = Math.pow(midPointX, 2);
  multipleVal += Math.pow(midPointY, 2);
  multipleVal += Math.pow(midPointZ, 2);
  multipleVal = Math.pow(distance, 2) / multipleVal;

  if (isOutsideUS) {
    multipleVal *= 0.3;
  } else {
    multipleVal *= 2.0;
  }

  // Apply the vector length to get new mid-points
  const midX = midPointX + multipleVal * midPointX;
  const midY = midPointY + multipleVal * midPointY;
  const midZ = midPointZ + multipleVal * midPointZ;

  // Return set of coordinates
  return {
    start: {
      x: start.x,
      y: start.y,
      z: start.z,
    },
    mid: {
      x: midX,
      y: midY,
      z: midZ,
    },
    end: {
      x: end.x,
      y: end.y,
      z: end.z,
    },
  };
}

// Returns an object of 2D coordinates for projected 3D position
function getProjectedPosition(width, height, position) {
  /*
    Using the coordinates of a geolocation in the 3D space, this function will
    return the 2D coordinates using the camera projection method.
  */
  position = position.clone();
  const projected = position.project(camera.object);

  return {
    x: projected.x * width + width,
    y: -(projected.y * height) + height,
  };
}

// Returns an object of the azimuthal and polar angles of a given map latitude and longitude
function returnCameraAngles(latitude, longitude) {
  /*
    Note that the azimuthal angle ranges from 0 to PI, whereas the polar angle
    ranges from -PI (negative PI) to PI (positive PI).
    A small offset is added to the azimuthal angle as angling the camera directly on top of a point makes the lines appear flat.
  */
  const targetAzimuthalAngle = ((longitude + 90) * Math.PI) / 180;
  const targetPolarAngle = ((90 - latitude) * Math.PI) / 180;

  return {
    azimuthal: targetAzimuthalAngle + 0.05,
    polar: targetPolarAngle + 0.05,
  };
}

export function updateAnimationToggle(animationOn) {
  animations.geolocations.animating = animationOn;
}

export function updateDots(toggleState) {
  props.toggleState = toggleState;
  console.log(toggleState);
}

export function installGlobe(hqLoc, citiesData) {
  if (!document || !window.WebGLRenderingContext) {
    throw new Error(
      'Browser does not reach minimum requirements for globe (WebGL suport)',
    );
  }

  props.hqLoc = hqLoc;
  props.citiesData = citiesData;

  if ('hidden' in document) {
    document.addEventListener('visibilitychange', onFocusChange);
  } else if ('mozHidden' in document) {
    document.addEventListener('mozvisibilitychange', onFocusChange);
  } else if ('webkitHidden' in document) {
    document.addEventListener('webkitvisibilitychange', onFocusChange);
  } else if ('msHidden' in document) {
    document.addEventListener('msvisibilitychange', onFocusChange);
  } else if ('onfocusin' in document) {
    document.onfocusin = document.onfocusout = onFocusChange;
  } else {
    window.onpageshow = window.onpagehide = window.onfocus = window.onblur = onFocusChange;
  }

  setupScene();
}



// WEBPACK FOOTER //
// ./src/components/MapHalf/map-animation.js